import { useState, useRef } from 'react'
import { ArrowLeft, Loader2, Copy, Upload, FileText, Image as ImageIcon, CheckCircle, Sparkles } from 'lucide-react'
import { API_BASE_URL, USE_MOCK } from '../App'

interface GradingProps {
  onBack: () => void
}

type AssignmentType = 'essay' | 'short_answer' | 'math'
type UploadMode = 'text' | 'image'

interface GradingResult {
  score: number
  is_correct?: boolean
  correct_answer?: string
  overall_comment: string
  strengths: string[]
  improvements: Array<{ issue: string; suggestion: string }>
  teacher_comments: string[]
}

// Mock grading data
const generateMockGrading = (_content: string, _assignmentType: string): GradingResult => {
  return {
    score: 8,
    overall_comment: "Good work! You've demonstrated a solid understanding of the topic with clear reasoning.",
    strengths: [
      "Clear thesis statement that directly addresses the prompt",
      "Well-organized structure with logical flow between paragraphs",
      "Good use of specific examples to support arguments",
      "Strong vocabulary and sentence variety"
    ],
    improvements: [
      { issue: "Limited analysis of counterarguments", suggestion: "Consider adding a paragraph addressing alternative perspectives to strengthen your position" },
      { issue: "Some transitions between ideas could be smoother", suggestion: "Use more transitional phrases to guide the reader through your reasoning" }
    ],
    teacher_comments: [
      "Excellent work on this assignment! Your research is thorough and your arguments are well-supported.",
      "I particularly enjoyed your analysis in the third paragraph — very insightful!",
      "Keep up the great work! Next time, try to explore more diverse perspectives."
    ]
  }
}

function Grading({ onBack }: GradingProps) {
  const [uploadMode, setUploadMode] = useState<UploadMode>('text')
  const [assignmentType, setAssignmentType] = useState<AssignmentType>('essay')
  const [content, setContent] = useState('')
  const [imageBase64, setImageBase64] = useState('')
  const [imagePreview, setImagePreview] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<GradingResult | null>(null)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageUpload = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const base64 = e.target?.result as string
      setImageBase64(base64)
      setImagePreview(base64)
    }
    reader.readAsDataURL(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
      handleImageUpload(file)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const hasContent = uploadMode === 'text' ? content.trim() : imageBase64
    if (!hasContent) return

    setLoading(true)
    setError('')
    setResult(null) // 清空上次结果，防止空白页

    if (USE_MOCK) {
      setTimeout(() => {
        setResult(generateMockGrading(content, assignmentType))
        setLoading(false)
      }, 2000)
      return
    }

    try {
      const body = uploadMode === 'text'
        ? { content, assignment_type: assignmentType, subject: 'English' }
        : { content: imageBase64, assignment_type: assignmentType, subject: 'English', is_image: true }

      const response = await fetch(`${API_BASE_URL}/api/grading/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      const data = await response.json()
      if (data.success) {
        try {
          // 尝试提取 JSON（AI 有时会包裹在 markdown 代码块里）
          let raw = data.data || '{}'
          // 去除可能的 markdown 代码块
          raw = raw.replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/```\s*$/i, '').trim()
          const parsed = JSON.parse(raw)
          // 确保必要字段存在
          setResult({
            score: Number(parsed.score) || 0,
            is_correct: parsed.is_correct,
            correct_answer: parsed.correct_answer || '',
            overall_comment: parsed.overall_comment || '',
            strengths: Array.isArray(parsed.strengths) ? parsed.strengths : [],
            improvements: Array.isArray(parsed.improvements) ? parsed.improvements : [],
            teacher_comments: Array.isArray(parsed.teacher_comments) ? parsed.teacher_comments : [],
          })
        } catch (parseErr) {
          // JSON 解析失败，显示原始文本
          setResult({
            score: 0,
            is_correct: undefined,
            correct_answer: '',
            overall_comment: data.data || 'Unable to parse feedback.',
            strengths: [],
            improvements: [],
            teacher_comments: [],
          })
        }
      } else {
        setError(data.error || 'Failed to analyze assignment')
      }
    } catch (err) {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const copyFeedback = () => {
    if (!result) return
    const feedback = `Score: ${result.score}/10\n${result.overall_comment}\n\nStrengths:\n${result.strengths.map(s => `- ${s}`).join('\n')}\n\nAreas for Improvement:\n${result.improvements.map(i => `- ${i.issue}: ${i.suggestion}`).join('\n')}\n\nTeacher Comments:\n${result.teacher_comments.map(c => `- ${c}`).join('\n')}`
    navigator.clipboard.writeText(feedback)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="min-h-screen">
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 w-full px-6 py-4 flex items-center gap-4 backdrop-blur-md bg-white/5 border-b border-white/10">
        <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-xl transition-all text-white/80 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-bold text-white">EduSpark</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Page Title */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-sm mb-4">
              <CheckCircle className="w-4 h-4" />
              <span>AI Assignment Grader</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Grade Student Assignment</h1>
            <p className="text-white/50">Paste text or upload an image — AI will analyze and provide detailed feedback</p>
          </div>

          {!result ? (
            <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Mode Tabs */}
                <div className="flex gap-2 p-1 bg-white/5 rounded-xl">
                  <button type="button" onClick={() => setUploadMode('text')}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${uploadMode === 'text' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white/80'}`}>
                    <FileText className="w-4 h-4" /> Paste Text
                  </button>
                  <button type="button" onClick={() => setUploadMode('image')}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${uploadMode === 'image' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white/80'}`}>
                    <ImageIcon className="w-4 h-4" /> Upload Image
                  </button>
                </div>

                {/* Content Input */}
                {uploadMode === 'text' ? (
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Student Work *</label>
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Paste the student's assignment here..."
                      rows={10}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all resize-none"
                      required
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Upload Image *</label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                    />
                    {imagePreview ? (
                      <div className="relative rounded-xl overflow-hidden border border-white/10">
                        <img src={imagePreview} alt="Preview" className="w-full max-h-64 object-contain bg-black/20" />
                        <button type="button" onClick={() => { setImageBase64(''); setImagePreview('') }}
                          className="absolute top-2 right-2 px-3 py-1 bg-red-500/80 text-white text-sm rounded-lg hover:bg-red-500">
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        onDrop={handleDrop}
                        onDragOver={(e) => e.preventDefault()}
                        className="border-2 border-dashed border-white/20 rounded-xl p-12 text-center hover:border-emerald-400/50 transition-colors cursor-pointer"
                      >
                        <Upload className="w-12 h-12 text-white/30 mx-auto mb-4" />
                        <p className="text-white/60 mb-2">Drag and drop an image here, or click to browse</p>
                        <p className="text-sm text-white/30">Supported: PNG, JPG, JPEG, WEBP</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Assignment Type */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-3">Assignment Type</label>
                  <div className="flex gap-3">
                    {[{ value: 'essay', label: 'Essay' }, { value: 'short_answer', label: 'Short Answer' }, { value: 'math', label: 'Math Problem' }].map((type) => (
                      <button key={type.value} type="button"
                        onClick={() => setAssignmentType(type.value as AssignmentType)}
                        className={`flex-1 py-3 rounded-xl border transition-all font-medium ${assignmentType === type.value ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}>
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
                    <p className="text-red-300 text-sm">{error}</p>
                  </div>
                )}

                <button type="submit"
                  disabled={loading || (uploadMode === 'text' ? !content.trim() : !imageBase64)}
                  className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2">
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Analyzing...</>
                  ) : (
                    <>Get AI Feedback <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg></>
                  )}
                </button>
              </form>
            </div>
          ) : (
            <div className="bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 overflow-hidden">
              {/* Result Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 bg-white/5">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${result.is_correct === true ? 'bg-gradient-to-br from-emerald-400 to-teal-600' : result.is_correct === false ? 'bg-gradient-to-br from-red-400 to-rose-600' : 'bg-gradient-to-br from-blue-400 to-purple-600'}`}>
                    <span className="text-2xl font-bold text-white">{result.score}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-white text-lg">{result.score}/10</p>
                      {result.is_correct !== undefined && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${result.is_correct ? 'bg-emerald-500/30 text-emerald-300' : 'bg-red-500/30 text-red-300'}`}>
                          {result.is_correct ? '✓ Correct' : '✗ Incorrect'}
                        </span>
                      )}
                    </div>
                    <p className="text-white/70 text-sm max-w-md">{result.overall_comment}</p>
                  </div>
                </div>
                <button onClick={copyFeedback}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${copied ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                  {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy Feedback'}
                </button>
              </div>

              {/* Result Content */}
              <div className="p-6 space-y-6">
                {/* Correct Answer */}
                {result.correct_answer && (
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-blue-300 mb-2 flex items-center gap-2">
                      <span>📝</span> Correct Answer / Model Answer
                    </h3>
                    <p className="text-white/80 whitespace-pre-wrap">{result.correct_answer}</p>
                  </div>
                )}
                {result.strengths.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-400 rounded-full"></span> Strengths
                    </h3>
                    <ul className="space-y-2">
                      {result.strengths.map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-white/80">
                          <span className="text-emerald-400 mt-0.5">✓</span>{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.improvements.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-400 rounded-full"></span> Areas for Improvement
                    </h3>
                    <ul className="space-y-3">
                      {result.improvements.map((item, i) => (
                        <li key={i} className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                          <p className="font-medium text-white mb-1">{item.issue}</p>
                          <p className="text-sm text-white/60">💡 {item.suggestion}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.teacher_comments.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-400 rounded-full"></span> Sample Comments (Click to Copy)
                    </h3>
                    <div className="space-y-3">
                      {result.teacher_comments.map((comment, i) => (
                        <div key={i}
                          onClick={() => navigator.clipboard.writeText(comment)}
                          className="p-4 bg-white/5 border border-white/10 rounded-xl border-l-4 border-l-blue-400 cursor-pointer hover:bg-white/10 transition-all">
                          <p className="text-white/80">{comment}</p>
                          <p className="text-xs text-white/30 mt-2">Click to copy</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-white/10">
                <button onClick={() => { setResult(null); setContent(''); setImageBase64(''); setImagePreview('') }}
                  className="text-white/50 hover:text-white transition-colors text-sm">
                  ← Grade Another Assignment
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default Grading
